import { Button, Col, DatePicker, Flex, Input, Row, Select, Space, Table, Tabs } from 'antd'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { getAddInvoiceDetailsByType, saveInvoice as save, getInvoice, getInvoiceAuditHistory, changeInvoiceStatus, getStatusById } from '../../service/httpsCalls';
import dayjs from "dayjs";
import type { TableProps } from 'antd';
import Card from 'antd/es/card/Card';
import { SorterResult } from 'antd/es/table/interface';
import { LoaderSpinner } from '../../components/common/Loader';
import TextArea from 'antd/es/input/TextArea';
import { AuditTableColumns } from './tableConfig';
import "./billing.scss";
import CCAHCalculationGrids from '../../components/Billing/CCAHCalculationGrids';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface ClientData {
    id: number,
    name: string
}

interface FilterData extends ClientData {
    clientId: number
}

interface FormFields {
    invoiceClient: any | null,
    clientName?: string,
    invoiceNumber: string | any,
    startDate: string | any,
    endDate: string | any,
    invoiceType: string | any,
    invoiceTypeName?: string,
    invoiceDate: string | any,
    status: string | any,
    poNumber: string | any,
    paymentDueDate: string | any
}

type StatusValue = { value: number; label: string };

interface PaginationObject {
    totalPages: number,
    loading: boolean,
    pageSize: number,
    currentPage: number,
    sortInfo: SorterResult<any>,
    menuVisible: boolean
}

export type ChildRef = {
    getInvoiceData: (arg?: string) => void;
    refreshAfterCCAHCalcSave: () => void;
};

const CreateInvoiceCCAH = forwardRef<ChildRef>(({ clientList, invoiceTypeLookup, selectedInvoice, action, filterData, modal, userAccess, statusList, parentCallback }, ref) => {
    const [form, setForm] = useState<FormFields>({
        invoiceClient: filterData?.filterClient ?? null,
        invoiceNumber: '',
        startDate: '',
        endDate: '',
        invoiceType: null,
        invoiceDate: null,
        status: '',
        poNumber: '',
        paymentDueDate: null
    });
    const [globalAPICalled, setGlobalAPICalled] = useState(false);
    const [invoiceTypes, setInvoiceTypes] = useState<FilterData[]>([]);
    const [modalSubmitted, setModalSubmitted] = useState(false);
    const [editPermissions, setEditPermissions] = useState<any | null>(null)
    const [auditHistory, setAuditHistory] = useState([]);

    const [loader, setLoader] = useState({
        save: false,
        status: false
    });

    const [loading, setLoading] = useState(false);

    const [statusForm, setStatusForm] = useState<{
        visible: boolean;
        status: StatusValue | null;
        note: string;
        statusList: any[];
    }>({
        visible: false,
        status: null,
        note: "",
        statusList: [],
    });

    //pagination object
    let paginationObject: PaginationObject = {
        totalPages: 0,
        loading: false,
        pageSize: 5,
        currentPage: 1,
        sortInfo: {},
        menuVisible: false
    }

    const [auditTable, setAuditTable] = useState(paginationObject);
    const [position, setPosition] = useState({
        x: 0,
        y: 0
    });
    const [invoiceInfo, setInvoiceInfo] = useState<any | null>(null);
    const auditColumns = AuditTableColumns;

    const [calcRefreshToken, setCalcRefreshToken] = useState(0);

    const [statusMeta, setStatusMeta] = useState<{
        invoiceId: number | null;
        invoiceStatusId?: number;
        note?: string;
        allowedStatusIds?: number[];
    } | null>(null);

    function refreshStatusMetaSilently() {
        getStatusById(selectedInvoice.id, (res: any) => {
            if (res?.hasError) {
                return;
            }

            const allowedIds =
                res?.statusIds
                    ? res.statusIds.split(',').map((y: any) => parseInt(y, 10))
                    : [];

            setStatusMeta({
                invoiceId: selectedInvoice.id,
                invoiceStatusId: res?.invoiceStatusId,
                note: res?.note,
                allowedStatusIds: allowedIds,
            });
        });
    }
    function validateModal() {
        var isValid = true;
        var errorMessage = '';
        setModalSubmitted(true);
        if (!(form.invoiceClient && form.invoiceType && form.invoiceNumber && form.startDate && form.endDate && form.invoiceDate && form.poNumber)) {
            isValid = false;
            errorMessage = "Please fill required details"
        }
        return { isValid, errorMessage };
    }

    function setModalData() {
        if (modal && !form.invoiceClient && filterData?.filterClient) {
            clientSelected(filterData?.filterClient, false);
            filterData?.filterType && getInvoiceDetails(filterData?.filterType, false, filterData?.filterClient);
        } else if (modal && form.invoiceClient && form.invoiceClient == filterData?.filterClient && !form.invoiceType) {
            setInvoiceTypes(invoiceTypeLookup.filter((x: any) => x.clientId == form.invoiceClient));
            action != 'add' && filterData?.filterType && getInvoiceDetails(filterData?.filterType, false);
        }
    }

    function cancelModal(e?: any) {
        clearModalFields();
        selectedInvoice = null;
        setAuditHistory([])
        setModalSubmitted(false);
        setEditPermissions(null);
        setInvoiceInfo(null);
        setLoader({
            save: false,
            status: false
        })
        setStatusForm({
            note: '',
            status: null,
            visible: false,
            statusList: []
        })
        modal = false;
        setAuditTable(paginationObject);

        if (e) {
            parentCallback({
                isCancel: true
            })
        }
    }

    function saveInvoice() {
        if (validateModal().isValid) {
            setLoader({
                ...loader,
                save: true
            })
            save({
                "id": selectedInvoice?.id ?? null,
                "clientId": parseInt(form.invoiceClient),
                "invoiceTypeId": parseInt(form.invoiceType),
                "invoiceDate": dayjs(form.invoiceDate).format('YYYY-MM-DD'),
                "invoiceNumber": form.invoiceNumber,
                "fromDos": dayjs(form.startDate).format('YYYY-MM-DD'),
                "toDos": dayjs(form.endDate).format('YYYY-MM-DD'),
                "poNumber": form.poNumber,
                "paymentDueDate": dayjs(form.paymentDueDate).format('YYYY-MM-DD')
            }, (res: any) => {
                setLoader({
                    ...loader,
                    save: false,
                })
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                    parentCallback({
                        isInvalid: true,
                        errorMessage: errorMsg
                    })
                } else {
                    if (action != 'edit') {
                        cancelModal();
                        parentCallback({
                            isInvalid: false,
                            clientId: form.invoiceClient,
                            invoiceTypeId: form.invoiceType,
                            message: 'Invoice saved successfully!'
                        })
                    }
                    else {
                        parentCallback({
                            isInvalid: false,
                            showMessage: true,
                            message: 'Invoice saved successfully!'
                        });
                        getAuditHistory();
                    }
                }
            })
        } else {
            parentCallback({
                isInvalid: true,
                errorMessage: validateModal().errorMessage
            })
        }
    }

    function getAuditHistory() {
        setAuditTable({
            ...auditTable,
            loading: true
        })
        setAuditHistory([]);
        getInvoiceAuditHistory({
            pageIndex: auditTable.currentPage,
            pageSize: auditTable?.pageSize,
            sortField: auditTable.sortInfo.order ? auditTable.sortInfo.field : null,
            sortDescending: auditTable.sortInfo.order == 'descend',
            id: selectedInvoice?.id
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
                setAuditTable({
                    ...auditTable,
                    loading: false,
                    totalPages: 1
                });
            } else {
                setAuditHistory(res.items);
                setAuditTable({
                    ...auditTable,
                    loading: false,
                    totalPages: Math.ceil(res.totalRecords / (auditTable.pageSize / auditTable.pageSize))
                })
            }
        })
    }

    function getInvoiceData() {
        setLoading(true);
        getInvoice({
            id: selectedInvoice.id,
            clientId: selectedInvoice.clientId
        }, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
            } else {
                setLoading(false);
                setInvoiceInfo(res.info);
                parentCallback({
                    canChangeTab: true,
                    calculate: {
                        invoiceInfo: { ...res.info, id: selectedInvoice.id },
                        canCalculate: res.actions.canCalculate
                    }
                });
                setInvoiceTypes(invoiceTypeLookup.filter((x: any) => x.clientId == res?.info?.clientId));

                setEditPermissions({
                    ...res.actions
                });
            }
        })
    }

    useImperativeHandle(ref, () => ({
        getInvoiceData,
        refreshAfterCCAHCalcSave: () => {
            getInvoiceData();
            getAuditHistory();
            setCalcRefreshToken((t) => t + 1);
            refreshStatusMetaSilently();
        },
    }));

    useEffect(() => {
        if (clientList.length && invoiceTypes.length && invoiceInfo) {
            setForm({
                invoiceDate: dayjs(invoiceInfo.invoiceDate),
                invoiceNumber: invoiceInfo.invoiceNumber,
                startDate: dayjs(invoiceInfo.fromDos),
                endDate: dayjs(invoiceInfo.toDos),
                invoiceClient: invoiceInfo.clientId.toString(),
                invoiceType: invoiceInfo.invoiceTypeId.toString(),
                status: invoiceInfo.status,
                clientName: invoiceInfo.clientName,
                invoiceTypeName: invoiceInfo.invoiceType,
                poNumber: invoiceInfo.poNumber,
                paymentDueDate: dayjs(invoiceInfo.paymentDueDate)
            });
        }

        if (invoiceTypes.length && filterData?.filterType && action == 'add') {
            getInvoiceDetails(filterData?.filterType, false);
            filterData.filterType = null;
        }
    }, [clientList, invoiceTypes, invoiceInfo])

    useEffect(() => {
        if (action == 'edit' && modal == true) {
            if (!globalAPICalled && selectedInvoice?.id) {
                setGlobalAPICalled(true);
            }
        } else if (action == 'cancel') {
            cancelModal();
        }
        if (action == 'add' && modal == true) {
            setModalData();
        }
    }, [action]);

    useEffect(() => {
        if (globalAPICalled) {
            getAuditHistory();
            getInvoiceData();
        }
    }, [globalAPICalled]);

    function clearModalFields() {
        setForm({
            ...form,
            invoiceDate: null,
            invoiceNumber: '',
            startDate: '',
            endDate: '',
            invoiceClient: null,
            invoiceType: null,
            poNumber: '',
            paymentDueDate: null
        });
        setInvoiceTypes([]);
    }

    function clientSelected(e: any, isPopupOpen: boolean) {
        if (e) {
            setForm({
                ...form,
                invoiceClient: e
            })
            setInvoiceTypes(invoiceTypeLookup.filter((x: any) => x.clientId == e));
            if (isPopupOpen) {
                parentCallback({
                    changeClient: true,
                    clientId: e
                });
            }
        }
    }

    function getInvoiceDetails(e: any, isPopupOpen: boolean, c?: any) {
        if (!e) { return }
        setLoading(true);
        getAddInvoiceDetailsByType({
            clientId: form.invoiceClient ?? c,
            invoiceTypeId: e
        }, (res: any) => {
            setLoading(false);
            if (res?.hasError) {
                setForm({
                    ...form,
                    invoiceType: e,
                    invoiceClient: form.invoiceClient ?? c,
                    invoiceTypeName: invoiceTypes?.length ? invoiceTypes.find((x) => x.id == e)?.name : form.invoiceTypeName
                });
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                })
            } else if (res) {
                setForm({
                    ...form,
                    invoiceDate: dayjs(res.invoiceDate),
                    invoiceNumber: res.invoiceNumber,
                    poNumber: res.poNumber,
                    startDate: res.fromDos ? dayjs(res.fromDos) : '',
                    endDate: res.toDos ? dayjs(res.toDos) : '',
                    invoiceType: e,
                    invoiceClient: form.invoiceClient ?? c,
                    invoiceTypeName: invoiceTypes?.length ? invoiceTypes.find((x) => x.id == e)?.name : form.invoiceTypeName,
                    paymentDueDate: dayjs(res.paymentDueDate)
                });
            }
        });
        if (isPopupOpen) {
            parentCallback({
                changeInvoiceType: true,
                invoiceType: e
            })
        }
    }

    useEffect(() => {
        if (globalAPICalled && selectedInvoice?.id && auditTable.pageSize != 0)
            getAuditHistory();
    }, [auditTable.currentPage, auditTable.pageSize, auditTable.sortInfo]);

    const auditTableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        setAuditTable({
            ...auditTable,
            currentPage: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? 10,
            totalPages: pagination.total ?? 100,
            sortInfo: sorter as SorterResult<any>
        })
    }
    function changeStatus() {
        // If we already fetched latest status meta for this invoice (eg after calc save),
        if (statusMeta?.invoiceId === selectedInvoice.id && statusMeta?.allowedStatusIds?.length) {

            const currentId = Number(statusMeta.invoiceStatusId);
            const currentItem = statusList.find((x: any) => Number(x.id) === currentId);

            setStatusForm({
                visible: true,
                status: currentItem ? { value: currentId, label: currentItem.name } : { value: currentId, label: String(currentId) },
                note: statusMeta.note ?? '',
                statusList: statusList.filter((x: any) => statusMeta.allowedStatusIds!.includes(x.id)),
            });
            return;
        }

        getStatusById(selectedInvoice.id, (res: any) => {
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
                setStatusForm({
                    visible: true,
                    status: form?.status,
                    note: '',
                    statusList: statusList
                })
            } else {
                const allowedIds = res?.statusIds
                    ? res.statusIds.split(',').map((y: any) => parseInt(y, 10))
                    : [];

                // keep cache warm for next time
                setStatusMeta({
                    invoiceId: selectedInvoice.id,
                    invoiceStatusId: res?.invoiceStatusId,
                    note: res?.note,
                    allowedStatusIds: allowedIds,
                });

                const currentId = Number(res.invoiceStatusId);
                const currentItem = statusList.find((x: any) => Number(x.id) === currentId);

                setStatusForm({
                    visible: true,
                    status: currentItem ? { value: currentId, label: currentItem.name } : { value: currentId, label: String(currentId) },
                    note: res?.note,
                    statusList: statusList.filter((x: any) => (x.clientId == form.invoiceClient && x.invoiceTypeId == form.invoiceType && res.statusIds.split(',').map((y: any) => parseInt(y)).includes(x.id)))
                })
            }
        })
    }

    function saveStatus() {
        setLoader({
            ...loader,
            status: true
        })

        changeInvoiceStatus({
            clientInvoiceSId: selectedInvoice?.id,
            clientId: invoiceInfo.clientId,
            invoiceType: invoiceInfo.invoiceType,
            statusId: Number(statusForm.status?.value),
            note: statusForm.note
        }, (res: any) => {
            setLoader({
                ...loader,
                status: false
            })
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!'
                parentCallback({
                    isInvalid: true,
                    errorMessage: errorMsg
                });
            } else {
                getAuditHistory();
                getInvoiceData();
                setStatusForm({ ...statusForm, visible: false });

                const msg = "Status changed successfully!"
                parentCallback({
                    isInvalid: false,
                    showMessage: true,
                    message: msg
                });
            }
        })
    }

    return (
        <div style={{ paddingTop: '15px' }}>
            <Card styles={{ body: { padding: '15px' } }} loading={loading}>
                {
                    <p
                        style={{
                            fontSize: 14,
                            color: 'rgba(0, 0, 0, 0.85)',
                            marginTop: '0px',
                            fontWeight: 500,
                        }}
                    >
                        Invoice Information
                    </p>
                }
                <div>
                    {
                        (action != 'edit' || editPermissions?.canEdit) &&
                        <>
                            <Row type="flex" style={{ paddingBottom: '1rem', background: '#fff' }}>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>Client</label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select a Client"
                                        onChange={(e) => clientSelected(e, true)}
                                        status={modalSubmitted && !form.invoiceClient ? 'error' : ''}
                                        value={form.invoiceClient}
                                        disabled={action == 'edit'}
                                    >
                                        {clientList && clientList.map((item: any) => (
                                            <Option key={item.id}>{item.name}</Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                            <Row type="flex" style={{ paddingBottom: '1rem', background: '#fff' }}>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice Type</label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Invoice Type"
                                        onChange={(e) => getInvoiceDetails(e, true)}
                                        value={form.invoiceType}
                                        disabled={!form.invoiceClient || action == 'edit'}
                                        status={modalSubmitted && !form.invoiceType ? 'error' : ''}
                                    >
                                        {invoiceTypes && invoiceTypes.map((item: any) => (
                                            <Option key={item.id}>{item.name}</Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice #</label>
                                    <Input value={form.invoiceNumber} status={modalSubmitted && !form.invoiceNumber ? 'error' : ''} disabled={!form.invoiceClient || !form.invoiceType} placeholder="Invoice #" style={{ width: '100%' }} allowClear
                                        onChange={(e) => {
                                            setForm({
                                                ...form,
                                                invoiceNumber: e?.target?.value
                                            })
                                        }}
                                    />
                                </Col>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>PO #</label>
                                    <Input value={form.poNumber} status={modalSubmitted && !form.poNumber ? 'error' : ''} disabled={!form.invoiceClient || !form.invoiceType} placeholder="PO #" style={{ width: '100%' }} allowClear
                                        onChange={(e) => {
                                            setForm({
                                                ...form,
                                                poNumber: e?.target?.value
                                            })
                                        }}
                                    />
                                </Col>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice Date</label>
                                    <DatePicker placeholder='Invoice Date' status={modalSubmitted && !form.invoiceDate ? 'error' : ''} value={form.invoiceDate} format="MM/DD/YYYY" disabled={!form.invoiceClient || !form.invoiceType} style={{ width: '100%' }}
                                        onChange={(date: any) => {
                                            const paymentDueDate = date ? new Date(date) : null;

                                            if (paymentDueDate)
                                                paymentDueDate.setDate(paymentDueDate.getDate() + 30);

                                            setForm({
                                                ...form,
                                                invoiceDate: date ? date : '',
                                                paymentDueDate: paymentDueDate ? dayjs(paymentDueDate) : ''
                                            })
                                        }}
                                    />
                                </Col>
                            </Row>
                            <Row type="flex" style={{ paddingBottom: '1rem', background: '#fff' }}>
                                <Col span={6} style={{ paddingRight: '0.5rem' }}>
                                    <label>Payment Due Date</label>
                                    <DatePicker placeholder='Payment Due Date' status={modalSubmitted && !form.paymentDueDate ? 'error' : ''} value={form.paymentDueDate} format="MM/DD/YYYY" disabled={true} style={{ width: '100%' }} />
                                </Col>
                                <Col span={12} style={{ paddingRight: '0.5rem' }}>
                                    <label>DOS Range</label>
                                    <RangePicker style={{ width: '100%' }}
                                        format="MM/DD/YYYY"
                                        value={[form.startDate, form.endDate]}
                                        status={modalSubmitted && !(form.startDate && form.endDate) ? 'error' : ''}
                                        disabled={!form.invoiceClient || !form.invoiceType}
                                        onChange={(dates: any) => {
                                            if (dates) {
                                                setForm({
                                                    ...form,
                                                    startDate: dates[0],
                                                    endDate: dates[1]
                                                })
                                            } else {
                                                setForm({
                                                    ...form,
                                                    startDate: '',
                                                    endDate: ''
                                                })
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                        </>
                    }
                    {
                        action == 'edit' && !editPermissions?.canEdit &&
                        <>
                            <Row type="flex" style={{ paddingBottom: '1rem', background: '#fff' }}>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Client</label>
                                    <br />
                                    {form.clientName}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice Type</label>
                                    <br />
                                    {form.invoiceTypeName}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice #</label><br />
                                    {form.invoiceNumber}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>PO #</label><br />
                                    {form.poNumber}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Invoice Date</label>
                                    <br />
                                    {form.invoiceDate ? dayjs(form.invoiceDate).format('MM/DD/YYYY') : ''}
                                </Col>
                            </Row>
                            <Row type="flex" style={{ paddingBottom: '1rem', background: '#fff' }}>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Payment Due Date</label>
                                    <br />
                                    {form.paymentDueDate ? dayjs(form.paymentDueDate).format('MM/DD/YYYY') : ''}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>From DOS</label>
                                    <br />
                                    {form.startDate ? dayjs(form.startDate).format('MM/DD/YYYY') : ''}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>To DOS</label>
                                    <br />
                                    {form.endDate ? dayjs(form.endDate).format('MM/DD/YYYY') : ''}
                                </Col>
                                <Col span={4} style={{ paddingRight: '0.5rem' }}>
                                    <label>Status</label>
                                    <br />
                                    {form.status}
                                </Col>
                            </Row>
                        </>
                    }
                    <Flex justify="end">
                        <Space>
                            {((action != 'edit' && userAccess?.canAdd) || (action == 'edit' && editPermissions?.canEdit)) &&
                                <>
                                    <Button key="3" onClick={e => cancelModal(e)} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                                        Cancel
                                    </Button>
                                    <Button key="1" onClick={e => saveInvoice()} style={{ backgroundColor: "#ff4d4f", color: "#fff" }}>Save</Button>
                                </>
                            }
                            {
                                editPermissions?.canChangeStatus &&
                                <Button key="changeStatusBtn" disabled={statusForm.visible} onClick={e => changeStatus()} style={{ backgroundColor: "blue", color: "#fff" }}>Change Status</Button>
                            }
                        </Space>
                    </Flex>
                </div>
            </Card >
            {
                action == 'edit' &&
                <div style={{ padding: "1rem", background: '#eff1f5' }}></div>
            }
            {
                action == 'edit' && statusForm.visible &&
                <div style={{ background: "#eff1f5" }}>
                    <Card styles={{ body: { padding: '15px' } }}>
                        <p
                            style={{
                                fontSize: 14,
                                color: 'rgba(0, 0, 0, 0.85)',
                                marginTop: '0px',
                                fontWeight: 500,
                            }}
                        >
                            Change Status
                        </p>
                        <Row style={{ paddingBottom: '0.5rem' }}>
                            <Col span={8}>
                                <label>Status</label>
                                <Select
                                    style={{ width: '95%' }}
                                    placeholder="Select a Status"
                                    labelInValue
                                    value={statusForm.status ?? undefined}
                                    onChange={(v) => setStatusForm({ ...statusForm, status: { value: Number(v.value), label: v.label }, note: "" })}
                                >
                                    {statusForm.statusList.map((item) => (
                                        <Option key={item.id} value={Number(item.id)}>
                                            {item.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col span={10}>
                                <label>Note</label>
                                <TextArea
                                    placeholder="Enter Note"
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                    value={statusForm.note}
                                    maxLength={300}
                                    onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                                />
                            </Col>
                        </Row>
                        <Flex justify="end">
                            <Space>
                                <Button key="saveNote" loading={loader.status} disabled={!statusForm.status} onClick={e => saveStatus()} style={{ backgroundColor: "blue", color: "#fff" }}>Save</Button>
                                <Button key="cancelNote" onClick={e => setStatusForm({ ...statusForm, visible: false })} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }}>Cancel</Button>
                            </Space>
                        </Flex>
                    </Card>
                    <div style={{ padding: "1rem" }}></div>
                </div>
            }
            {
                action == 'edit' && editPermissions && ((!editPermissions?.canEdit && !editPermissions?.canCalculate) || form.status === 'Processed') &&
                <div style={{ background: "#eff1f5" }}>
                    <CCAHCalculationGrids
                        invoiceInfo={{ ...(invoiceInfo || {}), id: selectedInvoice?.id }}
                        parentCallback={parentCallback}
                        readOnly={true}
                        refreshToken={calcRefreshToken}
                    />
                    <div style={{ padding: "1rem" }}></div>
                </div>
            }
            {
                action == 'edit' &&
                <Flex style={{ padding: "1rem", background: '#fff' }}>
                    <Tabs type="card" style={{ width: '100%' }}>
                        <TabPane tab="Audit History" key="auditHistoryTab">
                            <Table style={{ overflowY: "auto", height: "auto" }}
                                dataSource={auditHistory}
                                pagination={{
                                    defaultCurrent: 1,
                                    current: auditTable.currentPage,
                                    pageSize: auditTable.pageSize,
                                    total: auditTable.totalPages,
                                    showTotal: (total, range) => `${range[0] || 0}-${range[1] || 1} of ${total} items`,
                                    showSizeChanger: true,
                                }}
                                columns={auditColumns}
                                onChange={auditTableChanges}
                                rowKey="date"
                                loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: auditTable.loading }}
                                onHeaderRow={(columns, index) => {
                                    return {
                                        onContextMenu: (event) => {
                                            event.preventDefault();
                                            if (!auditTable.menuVisible) {
                                                document.addEventListener(`click`, function onClickOutside() {
                                                    setAuditTable({
                                                        ...auditTable,
                                                        menuVisible: false
                                                    });
                                                    document.removeEventListener(`click`, onClickOutside);
                                                });
                                            }
                                            setAuditTable({
                                                ...auditTable,
                                                menuVisible: true
                                            });
                                            setPosition({ x: event.clientX, y: event.clientY });
                                        }
                                    };
                                }}
                            />
                        </TabPane>
                    </Tabs>
                </Flex>
            }
        </div>
    )
})

export default CreateInvoiceCCAH;
